import { createRolloutSelectors, isFeatureEnabled } from '@lumapps/features';
import { isSuperAdmin, isUserAllowed } from '@lumapps/permissions';
import { createSelector } from '@lumapps/redux/reselect';
import { serviceSettingsSelector } from '@lumapps/service-settings/ducks/selectors';

import {
    AGENT_HUB_FEATURE_TOKEN,
    AGENT_HUB_EARLY_ACCESS_FEATURE_TOKEN,
    AGENT_HUB_DEVELOPMENT_FEATURE_TOKEN,
    AI_AGENTS_STUDIO_FF,
} from '../constants';

/**
 * Agent Hub rollout selectors following the inclusive hierarchy model.
 * - isGA: Production-ready features
 * - isEarlyAccess: Stable pre-release features (Early-Access + Development environments)
 * - isDevelopment: Unstable, in-progress features (Development environments only)
 */
export const agentHubRollout = createRolloutSelectors({
    ga: AGENT_HUB_FEATURE_TOKEN,
    earlyAccess: AGENT_HUB_EARLY_ACCESS_FEATURE_TOKEN,
    development: AGENT_HUB_DEVELOPMENT_FEATURE_TOKEN,
});

/**
 * Retrieves whether the current user is allowed to read/use RAG agents in the front office.
 *
 * Based on the `canReadRagAgents` permission returned by the backend at front-init.
 * Instance super-admins are allowed regardless (handled by `isUserAllowed`).
 */
export const canReadRagAgentSelector = isUserAllowed('canReadRagAgents');

/**
 * Retrieves whether the current user is allowed to read/use React agents in the front office.
 *
 * Based on the `canReadReactAgents` permission returned by the backend at front-init.
 * Instance super-admins are allowed regardless (handled by `isUserAllowed`).
 */
export const canReadReactAgentSelector = isUserAllowed('canReadReactAgents');

/**
 * Retrieves whether the current user can read/use at least one kind of agent (RAG or React).
 */
export const canReadAnyAgentSelector = createSelector(
    canReadRagAgentSelector,
    canReadReactAgentSelector,
    (canReadRagAgent, canReadReactAgent) => canReadRagAgent || canReadReactAgent,
);

/**
 * Whether the agent library `/agent-library` route is accessible: Agent Hub GA FF on AND the
 * `canReadReactAgents` permission. Visibility-independent (see `canDisplayAgentLibraryIconSelector`).
 */
export const canAccessAgentLibrarySelector = createSelector(
    agentHubRollout.isGA,
    canReadReactAgentSelector,
    (isEnabled, hasAgentReadPermission) => isEnabled && hasAgentReadPermission,
);

/**
 * Whether to show the agent library icon: accessible AND the org `askAgentLibrary` setting is on
 * (absent → hidden). Kept separate from the access gate so the route stays visibility-independent.
 */
export const canDisplayAgentLibraryIconSelector = createSelector(
    canAccessAgentLibrarySelector,
    serviceSettingsSelector,
    (canAccessAgentLibrary, serviceSettings) =>
        canAccessAgentLibrary && (serviceSettings?.askAgentLibrary?.isEnabled ?? false),
);

/**
 * This FF enables the "agent creation" feature on the product.
 */
export const agentStudioEnabledSelector = isFeatureEnabled(AI_AGENTS_STUDIO_FF);

/**
 * Retrieves whether the user can access the Agent analytics/metrics.
 */
export const agentAnalyticsEnabledSelector = agentHubRollout.isGA;

/**
 * Retrieves whether the user can access the agent hub admin.
 */
export const canAccessAgentHubAdminSelector = createSelector(
    agentHubRollout.isGA,
    isSuperAdmin,
    (agentHubEnabled, userIsSuperAdmin) => userIsSuperAdmin && agentHubEnabled,
);

/** Retrieves whether the user can access the agent creation page */
export const canAccessAgentCreationAdminSelector = createSelector(
    canAccessAgentHubAdminSelector,
    agentStudioEnabledSelector,
    (canAccessToAdmin, isAgentStudioEnabled) => canAccessToAdmin && isAgentStudioEnabled,
);
